import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheck, FiMessageCircle, FiHelpCircle, FiChevronDown } from 'react-icons/fi';
import api from '../api';

const C = {
  rose: '#e3425c', roseLight: 'rgba(227,66,92,0.08)', roseDeep: '#c94d5c',
  gold: '#d4a853', goldLight: 'rgba(212,168,83,0.12)', goldDark: '#b8923e',
  dark: '#1c1c1e', darkLight: '#3a3a3c', cream: '#f7f7f8', creamDark: '#ebebed',
  white: '#ffffff', gray: '#8e8e93', grayLight: '#aeaeb2',
};

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => { api.get('/faqs').then(r => setFaqs(r.data)).catch(() => {}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) return;
    setLoading(true);
    try {
      await api.post('/enquiry', form);
      setSubmitted(true);
    } catch { alert('Failed to submit. Please try again.'); }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', borderRadius: '10px',
    border: `1px solid ${C.creamDark}`, fontSize: '14px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    color: C.dark, background: C.white, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .contact-input:focus { border-color: ${C.rose} !important; box-shadow: 0 0 0 3px ${C.roseLight}; }
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.creamDark}`, padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.dark, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to='/about' style={{ padding: '9px 16px', borderRadius: '8px', color: C.dark, textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>About</Link>
            <Link to='/register' style={{ padding: '9px 20px', borderRadius: '8px', background: C.rose, color: C.white, textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '64px 0 40px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 28px' }}>
          <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(34px, 4.5vw, 50px)', fontWeight: 700, color: C.dark, lineHeight: 1.1, marginBottom: '14px' }}>
            Get in <span style={{ color: C.rose }}>Touch</span>
          </h1>
          <p style={{ fontSize: '16px', color: C.gray, lineHeight: 1.7 }}>
            Have a question, need help, or want to partner with us? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Split Layout: Form Left, Info Right */}
      <section style={{ padding: '0 0 80px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'start' }}>

          {/* Left: Contact Form */}
          <div style={{ background: C.white, borderRadius: '20px', padding: '40px', border: `1px solid ${C.creamDark}`, boxShadow: '0 2px 20px rgba(0,0,0,0.04)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '26px', marginBottom: '6px', color: C.dark }}>
              Send us a message
            </h2>
            <p style={{ fontSize: '14px', color: C.gray, marginBottom: '24px' }}>We typically respond within a few hours</p>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${C.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <FiCheck size={28} style={{ color: C.gold }} />
                </div>
                <h4 style={{ fontSize: '20px', marginBottom: '8px', color: C.dark, fontFamily: 'Playfair Display, Georgia, serif' }}>Message Sent!</h4>
                <p style={{ color: C.gray, fontSize: '14px', lineHeight: 1.6 }}>Your enquiry has been received. Our team will get back to you shortly.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  style={{ marginTop: '20px', padding: '11px 22px', borderRadius: '8px', border: `1px solid ${C.creamDark}`, background: C.white, color: C.dark, cursor: 'pointer', fontWeight: 500, fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: C.dark }}>Name *</label>
                    <input className='contact-input' placeholder='Your Name' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: C.dark }}>Email *</label>
                    <input className='contact-input' placeholder='your@email.com' type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: C.dark }}>Phone (optional)</label>
                  <input className='contact-input' placeholder='+27 81 234 5678' value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: C.dark }}>Subject *</label>
                  <select className='contact-input' value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required style={{ ...inputStyle, color: form.subject ? C.dark : C.gray }}>
                    <option value=''>Select a subject</option>
                    <option value='General Enquiry'>General Enquiry</option>
                    <option value='Event Help'>Event Help</option>
                    <option value='Vendor Support'>Vendor Support</option>
                    <option value='Payment Issue'>Payment Issue</option>
                    <option value='Bug Report'>Bug Report</option>
                    <option value='Partnership'>Partnership</option>
                    <option value='Other'>Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: C.dark }}>Message *</label>
                  <textarea className='contact-input' placeholder='How can we help you?' rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <button type='submit' disabled={loading} style={{
                  padding: '15px', borderRadius: '10px', border: 'none',
                  background: C.rose, color: C.white, fontSize: '15px', fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  opacity: loading ? 0.6 : 1, transition: 'all 0.2s',
                }}>
                  <FiSend size={16} /> {loading ? 'Sending...' : 'Submit Enquiry'}
                </button>
              </form>
            )}
          </div>

          {/* Right: Contact Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Contact cards */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '28px 32px', border: `1px solid ${C.creamDark}` }}>
              <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '20px', marginBottom: '20px', color: C.dark }}>Contact Info</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${C.rose}10`, color: C.rose, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>
                    <FiPhone />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: C.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Phone / WhatsApp</div>
                    <a href='tel:+27613911942' style={{ fontSize: '15px', color: C.dark, fontWeight: 600, textDecoration: 'none' }}>+27 61 391 1942</a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${C.gold}10`, color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>
                    <FiMail />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: C.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Email</div>
                    <a href='mailto:info@grandgatherregistry.co.za' style={{ fontSize: '15px', color: C.dark, fontWeight: 600, textDecoration: 'none' }}>info@grandgatherregistry.co.za</a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${C.rose}10`, color: C.rose, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>
                    <FiMapPin />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: C.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Location</div>
                    <div style={{ fontSize: '15px', color: C.dark, fontWeight: 600 }}>South Africa</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${C.gold}10`, color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>
                    <FiClock />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: C.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Hours</div>
                    <div style={{ fontSize: '15px', color: C.dark, fontWeight: 600 }}>Mon–Fri 8am–6pm</div>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a href='https://wa.me/27613911942' target='_blank' rel='noopener noreferrer'
              style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 28px', background: '#25D366', borderRadius: '16px', textDecoration: 'none', color: C.white, boxShadow: '0 4px 16px rgba(37,211,102,0.3)' }}>
              <FiMessageCircle size={28} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '16px' }}>Chat on WhatsApp</div>
                <div style={{ fontSize: '13px', opacity: 0.85 }}>Quick response · Mon–Fri 8am–6pm</div>
              </div>
            </a>

            {/* FAQ mini */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '28px 32px', border: `1px solid ${C.creamDark}` }}>
              <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '18px', marginBottom: '16px', color: C.dark, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiHelpCircle style={{ color: C.rose }} /> Quick FAQ
              </h3>
              {faqs.length > 0 ? faqs.slice(0, 4).map(f => (
                <div key={f.id} style={{ borderBottom: `1px solid ${C.creamDark}` }}>
                  <div onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '12px 0' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: C.dark, flex: 1, paddingRight: '12px' }}>{f.question}</span>
                    <FiChevronDown size={16} style={{ color: C.gray, transition: 'transform 0.2s', transform: openFaq === f.id ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                  </div>
                  {openFaq === f.id && <p style={{ fontSize: '13px', color: C.gray, lineHeight: 1.7, margin: '0 0 12px' }}>{f.answer}</p>}
                </div>
              )) : (
                <p style={{ fontSize: '13px', color: C.grayLight, textAlign: 'center', padding: '12px' }}>FAQs loading...</p>
              )}
              <Link to='/help' style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '12px', fontSize: '13px', color: C.rose, fontWeight: 600, textDecoration: 'none' }}>
                View all FAQs →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '60px 0 40px', background: C.dark, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', marginBottom: '40px' }}>
            {/* Logo & Description */}
            <div style={{ maxWidth: '300px' }}>
              <img
                src="/ggr-logo-deck.png"
                alt="GGR"
                style={{ height: '70px', width: 'auto', filter: 'brightness(0) invert(1)', marginBottom: '16px' }}
              />
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: '16px' }}>
                South Africa's AI-powered event management platform. Plan weddings, lobola, birthdays and more with GiGi.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Product</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Features', 'How It Works', 'Pricing', 'Vendors'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    <Link to={`/#${item.toLowerCase().replace(' ', '-')}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Company</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['About', 'Contact', 'Careers', 'Partnerships'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    <Link to={`/${item.toLowerCase()}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Legal</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Privacy Policy', 'Terms of Service', 'FAQs'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    {item === 'FAQs' ? (
                      <a href="/FAQs.pdf" download style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</a>
                    ) : (
                      <Link to={`/${item.toLowerCase().replace(' ', '-')}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              &copy; 2026 Grand Gather Registry. Made with love in South Africa.
            </p>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['Facebook', 'Instagram', 'TikTok'].map((social) => (
                <a key={social} href={`https://${social.toLowerCase()}.com/grandgatherregistry`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>
                  {social}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}